import ConfirmModal from 'components/UI/ConfirmPopup.js';
import { usePerms } from 'contexts/PermsContext.js';
import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import { setSelectedDevice } from 'features/Integrations/state/thunks/deviceThunks.js';
import { canViewDeviceType } from 'features/Integrations/utils/integrationVisibility.utils.js';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Group, Panel } from 'react-resizable-panels';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from 'store/slices/Widget/widgetSlice.js';
import { ResizablePanelHandle } from 'utils/layout/panelResize.utils';
import { selectUpdatedSelectedDevice } from '../../state/selectors/deviceSelectors.js';
import { USGS_PROVISIONAL_STATEMENT } from './assets/USGSProvisionalStatment.jsx';
import './assets/connectedDevices.css';
import ConnectedDevicesExpandedButton from './components/ConnectedDevicesExpandedButton/ConnectedDevicesExpandedButton.jsx';
import ConnectedDevicesExpandedView from './components/ConnectedDevicesExpandedView/ConnectedDevicesExpandedView.jsx';
import DeviceGraph from './features/DeviceGraph/index.web.jsx';
import DeviceList from './features/DeviceList/index.web.jsx';
import DeviceMap from './features/DeviceMap/index.web.jsx';

export default function ConnectedDevices({
  showCurrentDevice,
  setShowCurrentDevice,
  startTime,
  endTime,
  selectedGroup,
  setShowDeviceSettings,
  setSelectedGroup,
  setStartTime,
  setEndTime,
}) {
  const updatedSelectedDevice = useSelector(selectUpdatedSelectedDevice);
  const dispatch = useDispatch();
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  // provisionalAccepted is the single source of truth (Redux global).
  // showDisclaimerModal is only for explicit manual re-opens; the modal auto-shows
  // via !provisionalAccepted without needing to mirror that value in local state.
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [expandedMapGraphView, setExpandedMapGraphView] = useState(false);
  const [expandedLayout, setExpandedLayout] = useState('stack');
  const [useResizableLayout, setUseResizableLayout] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 768,
  );
  const perms = usePerms();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setUseResizableLayout(window.innerWidth >= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderExpandButton = () => {
    const handleExpand = () => {
      setExpandedLayout('stack');
      setExpandedMapGraphView(true);
    };

    return <ConnectedDevicesExpandedButton onClick={handleExpand} />;
  };

  const renderGraph = ({
    showFullscreenAction = false,
    constrainControls = false,
    graphControlsAlignment = 'default',
  } = {}) => {
    const canViewStage = canViewDeviceType(
      {
        system_type: 'Flood',
        device_type: 'Stage',
      },
      perms,
      visibleDeviceTypes,
    );
    const canViewBuoy = visibleDeviceTypes?.Buoy !== false;
    const canViewUSGS = canViewDeviceType(
      {
        system_type: 'USGS',
        device_type: 'Monitoring Station',
      },
      perms,
      visibleDeviceTypes,
    );

    if (!canViewStage && !canViewUSGS && !canViewBuoy) {
      return (
        <div className="d-flex flex-fill align-items-center justify-content-center text-center">
          Select a Device
        </div>
      );
    }

    return (
      <DeviceGraph
        startTime={startTime}
        endTime={endTime}
        setShowCurrentDevice={setShowCurrentDevice}
        setShowProvisional={setShowDisclaimerModal}
        expandButton={showFullscreenAction ? renderExpandButton() : null}
        constrainControls={constrainControls}
        graphControlsAlignment={graphControlsAlignment}
      />
    );
  };

  const renderMapPanel = ({ expanded = false } = {}) => (
    <div
      className={`${expanded ? '' : 'connected-devices-panel '}d-flex flex-column h-100 min-h-0`}
    >
      <DeviceMap
        setShowCurrentDevice={setShowCurrentDevice}
        setShowDeviceSettings={setShowDeviceSettings}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        setShowProvisional={setShowDisclaimerModal}
        usgsMapWidget
      />
    </div>
  );

  const renderGraphPanel = ({
    showFullscreenAction = false,
    constrainControls = false,
    graphControlsAlignment = 'default',
    expanded = false,
  } = {}) => (
    <div
      className={`${
        expanded ? '' : 'connected-devices-panel connected-devices-graph '
      }d-flex flex-column h-100 min-h-0`}
    >
      {renderGraph({ showFullscreenAction, constrainControls, graphControlsAlignment })}
    </div>
  );

  const listPanel = (
    <div
      className={
        useResizableLayout
          ? 'd-flex flex-column flex-grow-1 overflow-hidden max-h-fit min-h-0 h-100'
          : 'd-flex flex-column w-100 overflow-visible'
      }
    >
      <DeviceList
        updateSelectedDevice={updatedSelectedDevice}
        startTime={startTime}
        endTime={endTime}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        showCurrentDevice={showCurrentDevice}
        setShowCurrentDevice={setShowCurrentDevice}
        onSelectedDeviceIndex={(index) => dispatch(setSelectedDevice({ index: index }))}
      />
    </div>
  );

  const disclaimerModal = (
    <ConfirmModal
      open={!provisionalAccepted || showDisclaimerModal}
      setOpen={setShowDisclaimerModal}
      options={['Continue']}
      callbacks={[
        () => {
          dispatch(setUsgsProvisonalAccepted(true));
          setShowDisclaimerModal(false);
        },
      ]}
      message={USGS_PROVISIONAL_STATEMENT}
      closeAction={() => {}}
      size="lg"
    />
  );

  const expandedOverlay = (
    <ConnectedDevicesExpandedView
      open={expandedMapGraphView}
      layout={expandedLayout}
      setLayout={setExpandedLayout}
      onClose={() => setExpandedMapGraphView(false)}
      renderMapPanel={renderMapPanel}
      renderGraphPanel={renderGraphPanel}
    />
  );

  if (!useResizableLayout) {
    return (
      <>
        <Row className="connected-devices-mobile-stack g-md-3 gap-3 gap-md-0 flex-md-nowrap">
          <Col xs={12} md={7} className="connected-devices-mobile-stack-col d-flex flex-column">
            <div className="connected-devices-left d-flex flex-column gap-3">
              <div className="connected-devices-panel d-flex flex-column min-h-sm">
                {renderMapPanel()}
              </div>
              {disclaimerModal}
              <div className="connected-devices-panel connected-devices-graph d-flex flex-column">
                {renderGraph({ showFullscreenAction: true })}
              </div>
            </div>
          </Col>
          <Col xs={12} md={5} className="connected-devices-mobile-stack-col d-flex flex-column">
            {listPanel}
          </Col>
        </Row>
        {expandedOverlay}
      </>
    );
  }

  return (
    <>
      <Group
        orientation="horizontal"
        className="connected-devices-resizable-layout d-flex w-100 flex-grow-1 h-100"
        style={{ minHeight: 0, height: '100%' }}
      >
        <Panel
          defaultSize={58}
          minSize={35}
          className="d-flex flex-column h-100"
          style={{ minHeight: 0 }}
        >
          <Group
            orientation="vertical"
            className="connected-devices-left d-flex flex-column h-100"
            style={{ minHeight: 0 }}
          >
            <Panel
              defaultSize={38}
              minSize={24}
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              {renderMapPanel()}
            </Panel>

            <ResizablePanelHandle direction="horizontal" />

            <Panel
              defaultSize={62}
              minSize={32}
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              {renderGraphPanel({ showFullscreenAction: true })}
            </Panel>
          </Group>
        </Panel>

        <ResizablePanelHandle direction="vertical" />

        <Panel
          defaultSize={42}
          minSize={26}
          className="d-flex flex-column h-100"
          style={{ minHeight: 0 }}
        >
          {listPanel}
        </Panel>
      </Group>
      {disclaimerModal}
      {expandedOverlay}
    </>
  );
}
